import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  TextField,
  TextFieldProps,
} from "@mui/material";
import { User } from "datasheets/model/core/User";
import { loginAtom, userAtom } from "datasheets/model/atoms/UserAtoms";
import { useAtom, useSetAtom } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";
import { BDAvatar } from "./BDAvatar";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { UserMenu } from "./UserMenu";

function Login() {
  function handleLoginClick(event: React.MouseEvent<HTMLSpanElement>) {
    setModalIsOpen(true);
  }
  const [modalIsOpen, setModalIsOpen] = useState(false);
  function handleUserMenuClick(event: React.MouseEvent<HTMLSpanElement>) {
    setAnchorEl(event.currentTarget);
  }
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [user] = useAtom<User | null>(userAtom);
  const contents = user ? (
    <BDAvatar user={user} onClick={handleUserMenuClick} />
  ) : (
    <Button onClick={handleLoginClick}>Sign In</Button>
  );
  return (
    <Box className="Login" role="login">
      {contents}
      <LoginForm modalIsOpen={modalIsOpen} setModalIsOpen={setModalIsOpen} />
      <UserMenu anchorEl={anchorEl} setAnchorEl={setAnchorEl} />
    </Box>
  );
}

interface ModalFormProps {
  modalIsOpen: boolean;
  setModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}
function LoginForm({ modalIsOpen, setModalIsOpen }: ModalFormProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const login = useSetAtom(loginAtom);
  const usernameDiv = useRef<HTMLInputElement>(null);
  const passwordDiv = useRef<HTMLInputElement>(null);

  // Add handler for key presses
  const keypressHandler = useCallback(
    (event: KeyboardEvent) => {
      // if (event.key === "Escape") {
      //   //Should cancel dialog
      // }
      if (event.key === "Enter") {
        // If all fields are filled in, then submit, otherwise move focus to missing field
        if (!username) {
          // Move focus to username field
          if (usernameDiv.current) {
            usernameDiv.current.focus();
          }
        } else if (!password) {
          // Move focus to password field
          if (passwordDiv.current) {
            passwordDiv.current.focus();
          }
        } else {
          handleSubmitColumnChanges();
        }
      }
    },
    [username, password, login, setModalIsOpen]
  );

  useEffect(() => {
    document.addEventListener("keydown", keypressHandler, false);

    return () => {
      document.removeEventListener("keydown", keypressHandler, false);
    };
  }, [keypressHandler]);

  function handleSubmitColumnChanges() {
    login({ username, password, setModalIsOpen });
    handleCloseDialog();
  }

  function clearFields() {
    setUsername("");
    setPassword("");
  }

  function handleCloseDialog() {
    clearFields();
    setModalIsOpen(false);
  }
  return (
    <Dialog open={modalIsOpen} onClose={handleCloseDialog} role="loginForm">
      <DialogContent>
        <Stack spacing={2}>
          <TextField
            inputRef={usernameDiv}
            type="text"
            id="userName"
            label="Username"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
          />
          <FormControl>
            <InputLabel htmlFor="password">Password</InputLabel>
            <OutlinedInput
              type={showPassword ? "text" : "password"}
              inputRef={passwordDiv}
              id="password"
              label="Password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword((show) => !show)}
                    //onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
            />
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialog}>Cancel</Button>
        <Button onClick={handleSubmitColumnChanges} variant="outlined">
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default Login;
